import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "../components/ui/progress";

export default function TakeTest() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/tests/${slug}/start`).then(r => setTest(r.data)).catch((err) => {
      if (err.response?.status === 403) {
        toast.error("Anda perlu membeli akses tes ini");
        navigate(`/tests/${slug}`);
      } else {
        toast.error("Gagal memulai tes");
        navigate("/tests");
      }
    });
  }, [slug, navigate]);

  if (!test) return <div className="lumina-container py-20 text-center text-[#5C5870]">Memuat tes…</div>;
  if (!test.questions || test.questions.length === 0) return <div className="lumina-container py-20 text-center text-[#5C5870]">Pertanyaan tes belum ditambahkan oleh admin.</div>;

  const total = test.questions.length;
  const progress = ((step + 1) / total) * 100;
  const q = test.questions[step];

  const select = (idx) => setAnswers({ ...answers, [step]: idx });

  const next = () => {
    if (answers[step] === undefined) { toast.error("Pilih salah satu jawaban"); return; }
    if (step < total - 1) setStep(step + 1);
  };

  const submit = async () => {
    if (Object.keys(answers).length !== total) { toast.error("Lengkapi semua jawaban"); return; }
    setSubmitting(true);
    try {
      const arr = Array.from({ length: total }, (_, i) => answers[i]);
      const { data } = await api.post(`/tests/${slug}/submit`, { answers: arr });
      navigate(`/test-results/${data.id}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="lumina-container py-12 max-w-3xl" data-testid="take-test-page">
      <h1 className="font-serif text-3xl text-[#1A1728] mb-2">{test.title}</h1>
      <p className="text-sm text-[#5C5870] mb-6">Pertanyaan {step + 1} dari {total}</p>
      <Progress value={progress} className="h-2 mb-10" />
      <div className="lumina-card p-8 mb-6">
        <h2 className="font-serif text-2xl text-[#1A1728] mb-6 leading-snug">{q.text}</h2>
        <div className="space-y-3">
          {q.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => select(idx)}
              data-testid={`answer-${idx}`}
              className={`w-full text-left px-5 py-4 rounded-2xl border transition-all flex items-center justify-between ${
                answers[step] === idx
                  ? "border-[#6B4BA3] bg-[#E8F0FA] text-[#1A1728]"
                  : "border-[#E2DFED] hover:border-[#9D84D2]"
              }`}
            >
              <span>{opt.label}</span>
              {answers[step] === idx && <Check className="w-5 h-5 text-[#6B4BA3]" />}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-between">
        <button disabled={step === 0} onClick={() => setStep(step - 1)} className="lumina-btn-outline disabled:opacity-40" data-testid="prev-btn">
          <ArrowLeft className="w-4 h-4" /> Sebelumnya
        </button>
        {step < total - 1 ? (
          <button onClick={next} className="lumina-btn-primary" data-testid="next-btn">
            Selanjutnya <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} className="lumina-btn-primary" data-testid="submit-test-btn">
            {submitting ? "Memproses…" : "Lihat Hasil"} <Check className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
